import { useState, useEffect } from 'react';
import { definicoesAPI } from '../utils/api';
import { usePageTitle } from '../hooks/usePageTitle';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Settings, Loader2, Save, Building2, FileText } from 'lucide-react';
import Swal from 'sweetalert2';

export const Definicoes = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  usePageTitle('Definições');
  const [formData, setFormData] = useState({
    nome_empresa: '',
    logo_url: '',
    morada_empresa: '',
    telefone_empresa: '',
    email_empresa: '',
    texto_atestado: ''
  });

  useEffect(() => {
    fetchDefinicoes();
  }, []);

  const fetchDefinicoes = async () => {
    try {
      const response = await definicoesAPI.get();
      setFormData({
        nome_empresa: response.data.nome_empresa || '',
        logo_url: response.data.logo_url || '',
        morada_empresa: response.data.morada_empresa || '',
        telefone_empresa: response.data.telefone_empresa || '',
        email_empresa: response.data.email_empresa || '',
        texto_atestado: response.data.texto_atestado || ''
      });
    } catch (error) {
      console.error('Error fetching definicoes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSaving(true);
    try {
      await definicoesAPI.update(formData);
      Swal.fire({
        icon: 'success',
        title: 'Guardado!',
        text: 'Definições atualizadas com sucesso.',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error saving definicoes:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Não foi possível guardar as definições.',
        confirmButtonColor: '#0D9488'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="definicoes-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Definições</h1>
        <p className="text-slate-500 mt-1">Configure os dados da empresa e documentos</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Empresa */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-amber-600" />
              Dados da Empresa
            </CardTitle>
            <CardDescription>
              Estas informações aparecem nos documentos impressos (receitas, etiquetas, atestados)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nome_empresa" className="text-slate-700">
                  Nome da Empresa
                </Label>
                <Input
                  id="nome_empresa"
                  name="nome_empresa"
                  value={formData.nome_empresa}
                  onChange={handleChange}
                  placeholder="PharmaFlow"
                  data-testid="definicoes-nome-empresa"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefone_empresa" className="text-slate-700">
                  Telefone
                </Label>
                <Input
                  id="telefone_empresa"
                  name="telefone_empresa"
                  value={formData.telefone_empresa}
                  onChange={handleChange}
                  placeholder="210 000 000"
                  data-testid="definicoes-telefone"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="morada_empresa" className="text-slate-700">
                Morada
              </Label>
              <Input
                id="morada_empresa"
                name="morada_empresa"
                value={formData.morada_empresa}
                onChange={handleChange}
                placeholder="Rua da Farmácia, 123 - 1000-001 Lisboa"
                data-testid="definicoes-morada"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email_empresa" className="text-slate-700">
                Email
              </Label>
              <Input
                id="email_empresa"
                name="email_empresa"
                type="email"
                value={formData.email_empresa}
                onChange={handleChange}
                placeholder="geral@farmacia.pt"
                data-testid="definicoes-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="logo_url" className="text-slate-700">
                URL do Logo
              </Label>
              <Input
                id="logo_url"
                name="logo_url"
                value={formData.logo_url}
                onChange={handleChange}
                placeholder="https://exemplo.pt/logo.png"
                data-testid="definicoes-logo"
              />
              <p className="text-xs text-slate-500">
                URL de uma imagem para usar como logo nos documentos (opcional)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Texto Atestado */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <FileText className="h-5 w-5 text-amber-600" />
              Texto do Atestado
            </CardTitle>
            <CardDescription>
              Este texto é utilizado no documento de atestado médico
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="texto_atestado" className="text-slate-700">
                Texto Padrão
              </Label>
              <Textarea
                id="texto_atestado"
                name="texto_atestado"
                value={formData.texto_atestado}
                onChange={handleChange}
                rows={5}
                placeholder="Atesta-se para os devidos efeitos que..."
                data-testid="definicoes-texto-atestado"
              />
              <p className="text-xs text-slate-500">
                Este texto será incluído automaticamente em todos os atestados gerados
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            className="bg-amber-600 hover:bg-amber-700 text-white"
            disabled={saving}
            data-testid="definicoes-submit-btn"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                A guardar...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Guardar Definições
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};
